/* =============================================================================
 * AYA ConfettiBurst — H_UIPOLISH_V1_L3 Palier 1
 * -----------------------------------------------------------------------------
 * Burst confetti déclenché impérativement via ref.fire().
 * - forwardRef + useImperativeHandle exposant { fire(overrides?), reset() }
 * - canvas-confetti chargé en import dynamique (perf : 0 charge initiale)
 * - Palette signature "emerald-gold" par défaut
 * - Respect prefers-reduced-motion : fire() devient no-op
 *
 * Props:
 *   - palette       : "emerald-gold" | "emerald" | "gold" | string[]
 *                     (default "emerald-gold")
 *   - particleCount : number (default 80)
 *   - origin        : { x, y } (default { x: 0.5, y: 0.5 })
 *   - spread        : number (default 70)
 *   - startVelocity : number (default 35)
 *   - scalar        : number (default 0.9)
 *
 * Usage:
 *   const ref = useRef(null);
 *   <ConfettiBurst ref={ref} palette="emerald-gold" />
 *   ref.current?.fire();
 * ========================================================================== */

import React, {
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from "react";

const PALETTES = {
    "emerald-gold": [
        "#10b981",
        "#34d399",
        "#a7f3d0",
        "#f59e0b",
        "#fbbf24",
        "#fde68a",
    ],
    emerald: ["#059669", "#10b981", "#34d399", "#6ee7b7", "#a7f3d0"],
    gold: ["#d97706", "#f59e0b", "#fbbf24", "#fcd34d", "#fde68a"],
};

function resolvePalette(p) {
    if (Array.isArray(p) && p.length > 0) return p;
    return PALETTES[p] || PALETTES["emerald-gold"];
}

function prefersReducedMotion() {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    try {
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (_e) {
        return false;
    }
}

export const AyaConfettiBurst = forwardRef(function AyaConfettiBurst(
    {
        palette = "emerald-gold",
        particleCount = 80,
        origin = { x: 0.5, y: 0.5 },
        spread = 70,
        startVelocity = 35,
        scalar = 0.9,
    },
    ref
) {
    const confettiModRef = useRef(null);
    const loadingRef = useRef(false);

    const loadModule = useCallback(async () => {
        if (confettiModRef.current) return confettiModRef.current;
        if (loadingRef.current) return null;
        loadingRef.current = true;
        try {
            const mod = await import("canvas-confetti");
            confettiModRef.current = mod.default || mod;
            return confettiModRef.current;
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[AyaConfettiBurst] canvas-confetti load failed", e);
            return null;
        } finally {
            loadingRef.current = false;
        }
    }, []);

    const fire = useCallback(
        async (overrides = {}) => {
            if (prefersReducedMotion()) return;
            const confetti = await loadModule();
            if (!confetti) return;
            const colors = resolvePalette(overrides.palette ?? palette);
            confetti({
                particleCount: overrides.particleCount ?? particleCount,
                spread: overrides.spread ?? spread,
                startVelocity: overrides.startVelocity ?? startVelocity,
                scalar: overrides.scalar ?? scalar,
                origin: overrides.origin ?? origin,
                colors,
                ticks: 200,
                gravity: 0.9,
                drift: 0,
                disableForReducedMotion: true,
                zIndex: 1700,
            });
        },
        [loadModule, palette, particleCount, spread, startVelocity, scalar, origin]
    );

    const reset = useCallback(async () => {
        const confetti = await loadModule();
        if (!confetti) return;
        try {
            if (typeof confetti.reset === "function") confetti.reset();
        } catch (_e) {
            /* noop */
        }
    }, [loadModule]);

    useImperativeHandle(ref, () => ({ fire, reset }), [fire, reset]);

    // Composant invisible : c'est uniquement un "controller" impératif.
    return (
        <span
            data-testid="aya-confetti-burst"
            aria-hidden="true"
            style={{ display: "none" }}
        />
    );
});

export default AyaConfettiBurst;
